// src/modules/stock/StockEntryPage.js (Versão Completa - Com React Icons e Tradução)

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    doc,
    runTransaction,
    serverTimestamp,
    getCountFromServer,
    query,
    orderBy,
    limit,
    startAfter,
    where,
    getDoc
} from 'firebase/firestore';
import * as XLSX from 'xlsx';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiChevronDown,
    FiChevronUp,
    FiFileText,
    FiUpload,
    FiDownload,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiSearch,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiPackage,
    FiMapPin,
    FiTag,
    FiCalendar,
    FiClock,
    FiUsers,
    FiFolder,
    FiBox,
    FiDroplet,
    FiClipboard
} from 'react-icons/fi';
import { FaRegFileExcel, FaPlus, FaWater } from 'react-icons/fa';
import { MdCancel, MdEdit, MdDelete, MdAdd, MdSave } from 'react-icons/md';

import useAuth from '../../hooks/useAuth';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    FormModal,
    ConfirmModal,
    SearchBar,
    Pagination,
    TableSkeleton,
    EmptyState,
    Toast,
    PageHeader
} from '../../components/common';

import styles from './Logistics.module.css';

const ITEMS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 300;

// Constantes das coleções
const STOCK_ENTRIES_COLLECTION = 'stock-entries';
const CURRENT_STOCK_COLLECTION = 'current-stock';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const PRODUCTS_COLLECTION = 'products';
const LOCATIONS_COLLECTION = 'localizacoes';
const PRODUCT_TYPE_COLLECTION_PATH = 'parameters/types/tipo-produto';
const CATEGORY_COLLECTION_PATH = 'parameters/data/product-categories';

// Configuração da Água
const WATER_MATERIALS = ['MP001', 'AGUA001', 'WATER001'];
const WATER_MATERIAL_IDS = [];
const WATER_QUANTITY_KEY = 'water_infinite';

const isWaterMaterial = (material) => {
    if (!material) return false;
    if (material.code && WATER_MATERIALS.includes(material.code.toUpperCase())) return false;
    if (material.id && WATER_MATERIAL_IDS.includes(material.id)) return true;
    return false;
};

const isProductMaterial = (material) => {
    return material?.isProduct === true;
};

// Cache service
class CacheService {
    constructor(ttl = 300000) {
        this.cache = new Map();
        this.ttl = ttl;
    }
    get(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.ttl) return cached.data;
        return null;
    }
    set(key, data) {
        this.cache.set(key, { data, timestamp: Date.now() });
    }
}

const cache = new CacheService();

const useDebounce = (value, delay = DEBOUNCE_DELAY) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    try {
        const expiry = expirationDate.toDate ? expirationDate.toDate() : new Date(expirationDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return expiry < today;
    } catch {
        return false;
    }
};

const isNearExpiry = (expirationDate) => {
    if (!expirationDate) return false;
    try {
        const expiry = expirationDate.toDate ? expirationDate.toDate() : new Date(expirationDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        return expiry <= thirtyDaysFromNow && expiry >= today;
    } catch {
        return false;
    }
};

// --- Componente Modal de Importação de Planilha ---
const StockImportModal = ({
    isOpen,
    onClose,
    onImport,
    isImporting,
    file,
    setFile,
    category,
    setCategory,
    status,
    setStatus,
    t
}) => {
    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) setFile(selectedFile);
        if (setStatus) setStatus({ message: '', type: '' });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onImport();
    };

    return (
        <div className={styles.modalBackdrop}>
            <div className={styles.formContainer} style={{ maxWidth: '600px' }}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2>{t('pages.stock.import.title', 'Importar Planilha de Estoque')}</h2>
                        <p className={styles.modalSubtitle}>
                            {t('pages.stock.import.subtitle', 'Faça o upload da planilha para registrar múltiplas entradas de estoque')}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton} disabled={isImporting}>
                        <FiX size={24} />
                    </button>
                </div>

                {status?.message && (
                    <div className={`${styles.alert} ${styles[status.type]}`}>
                        {status.message}
                    </div>
                )}

                <form onSubmit={handleSubmit} className={styles.formContent}>
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>
                                <FiFileText size={20} />
                            </div>
                            <h3 className={styles.sectionTitle}>{t('pages.stock.import.configTitle', 'Configuração da Importação')}</h3>
                        </div>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                <label>{t('pages.stock.import.stockType', 'Tipo de Estoque (Destino)')} <span className={styles.required}>*</span></label>
                                <select
                                    className={`${styles.input} ${styles.select}`}
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    required
                                    disabled={isImporting}
                                >
                                    <option value="">{t('pages.stock.import.selectCategory', 'Selecione uma categoria...')}</option>
                                    <option value="mp_semi">{t('pages.stock.import.categoryMp', 'MP e Semi Acabado')}</option>
                                    <option value="embalagem_acabado">{t('pages.stock.import.categoryPackaging', 'Embalagem e Produto Acabado')}</option>
                                </select>
                                <small className={styles.fieldHint}>
                                    {t('pages.stock.import.categoryHint', 'Define quais localizações estarão disponíveis para o destino do estoque')}
                                </small>
                            </div>

                            <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                <label>{t('pages.stock.import.fileLabel', 'Planilha de Importação')} <span className={styles.required}>*</span></label>
                                <input
                                    type="file"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileChange}
                                    className={styles.input}
                                    required
                                    disabled={isImporting}
                                />
                                <small className={styles.fieldHint}>
                                    {t('pages.stock.import.fileHint', 'Formatos aceitos: .xlsx, .xls, .csv | Colunas esperadas: Código, Localização, Quantidade, Lote (opcional), Fabricado (opcional), Validade (opcional)')}
                                </small>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formActions}>
                        <button type="button" onClick={onClose} className={styles.secondaryButton} disabled={isImporting}>
                            <FiX size={18} /> {t('common.cancel', 'Cancelar')}
                        </button>
                        <button
                            type="submit"
                            className={styles.primaryButton}
                            disabled={isImporting || !file || !category}
                        >
                            {isImporting ? (
                                <>
                                    <FiRefreshCw size={18} className={styles.spinner} /> {t('pages.stock.import.processing', 'Processando...')}
                                </>
                            ) : (
                                <>
                                    <FaRegFileExcel size={18} /> {t('pages.stock.import.startImport', 'Iniciar Importação')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Componente de Conteúdo do Modal de Entrada ---
const StockEntryModalContent = ({
    isEditing,
    rawMaterialsList,
    locationsList,
    selectedRawMaterialId,
    setSelectedRawMaterialId,
    selectedLocationIds,
    setSelectedLocationIds,
    quantity,
    setQuantity,
    notes,
    setNotes,
    status,
    productTypes,
    categories,
    filterProductTypeId,
    setFilterProductTypeId,
    filterCategoryId,
    setFilterCategoryId,
    locationCategoryFilter,
    setLocationCategoryFilter,
    lotNumber,
    setLotNumber,
    expirationDate,
    setExpirationDate,
    manufacturerDate,
    setManufacturerDate,
    loading,
    t
}) => {
    const [materialSearchTerm, setMaterialSearchTerm] = useState('');
    const [isMaterialOpen, setIsMaterialOpen] = useState(false);
    const [locationSearchTerm, setLocationSearchTerm] = useState('');
    const [isLocationOpen, setIsLocationOpen] = useState(false);

    const materialInputRef = useRef(null);
    const locationInputRef = useRef(null);
    const modalContentRef = useRef(null);
    const materialDropdownRef = useRef(null);
    const locationDropdownRef = useRef(null);

    const [materialDropdownPos, setMaterialDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const [locationDropdownPos, setLocationDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    const selectedRawMaterial = rawMaterialsList?.find(r => r.id === selectedRawMaterialId);
    const isWater = selectedRawMaterial ? isWaterMaterial(selectedRawMaterial) : false;
    const isProductItem = selectedRawMaterial ? isProductMaterial(selectedRawMaterial) : false;

    const updateDropdownPosition = (ref, setter) => {
        if (ref && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setter({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (materialInputRef.current && !materialInputRef.current.contains(event.target)) {
                if (materialDropdownRef.current && !materialDropdownRef.current.contains(event.target)) {
                    setIsMaterialOpen(false);
                    if (!selectedRawMaterialId) {
                        setMaterialSearchTerm('');
                    }
                }
            }
            if (locationInputRef.current && !locationInputRef.current.contains(event.target)) {
                if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
                    setIsLocationOpen(false);
                    setLocationSearchTerm('');
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [selectedRawMaterialId]);

    useEffect(() => {
        if (isMaterialOpen) updateDropdownPosition(materialInputRef, setMaterialDropdownPos);
    }, [isMaterialOpen]);

    useEffect(() => {
        if (isLocationOpen) updateDropdownPosition(locationInputRef, setLocationDropdownPos);
    }, [isLocationOpen]);

    const filteredLocations = useMemo(() => {
        if (!locationsList) return [];
        let filtered = locationsList;
        if (locationCategoryFilter) {
            filtered = filtered.filter(loc => loc.category === locationCategoryFilter);
        }
        if (locationSearchTerm.trim()) {
            const searchLower = locationSearchTerm.toLowerCase();
            filtered = filtered.filter(loc =>
                loc.id.toLowerCase().includes(searchLower) ||
                (loc.description && loc.description.toLowerCase().includes(searchLower))
            );
        }
        return filtered;
    }, [locationsList, locationCategoryFilter, locationSearchTerm]);

    const filteredRawMaterials = useMemo(() => {
        if (!rawMaterialsList) return [];
        const lowerCaseSearchTerm = (materialSearchTerm || '').toLowerCase();
        return rawMaterialsList.filter(item => {
            const itemCode = (item.code || '').toLowerCase();
            const itemName = (item.commercialName || '').toLowerCase();
            const matchesSearch = itemCode.includes(lowerCaseSearchTerm) || itemName.includes(lowerCaseSearchTerm);
            if (!matchesSearch) return false;
            const itemTypes = item.productTypeIds || [];
            const matchesType = !filterProductTypeId || itemTypes.includes(filterProductTypeId);
            if (!matchesType) return false;
            const itemCategories = item.categoryIds || [];
            const matchesCategory = !filterCategoryId || itemCategories.includes(filterCategoryId);
            return matchesCategory;
        }).slice(0, 50);
    }, [rawMaterialsList, materialSearchTerm, filterProductTypeId, filterCategoryId]);

    const materialDisplayValue = selectedRawMaterial
        ? `${selectedRawMaterial.code} - ${selectedRawMaterial.commercialName}`
        : materialSearchTerm;

    const handleSelectMaterial = (option) => {
        setSelectedRawMaterialId(option.id);
        setMaterialSearchTerm('');
        setIsMaterialOpen(false);
        setSelectedLocationIds([]);
        setQuantity({});
        setLotNumber('');
        setExpirationDate('');
        setManufacturerDate('');
    };

    const handleMaterialInputChange = (e) => {
        const newTerm = e.target.value;
        setMaterialSearchTerm(newTerm);
        if (selectedRawMaterialId && selectedRawMaterial) {
            const matches = newTerm.toLowerCase().includes(selectedRawMaterial.code?.toLowerCase() || '') ||
                newTerm.toLowerCase().includes(selectedRawMaterial.commercialName?.toLowerCase() || '');
            if (!matches && newTerm !== '') {
                setSelectedRawMaterialId('');
            }
        }
        setIsMaterialOpen(true);
    };

    const handleMaterialFocus = () => {
        updateDropdownPosition(materialInputRef, setMaterialDropdownPos);
        setIsMaterialOpen(true);
    };

    const handleSelectLocation = (option) => {
        if (selectedLocationIds.includes(option.id)) {
            setSelectedLocationIds(prev => prev.filter(locId => locId !== option.id));
            setQuantity(prev => {
                const newQty = { ...prev };
                delete newQty[option.id];
                return newQty;
            });
        } else {
            setSelectedLocationIds(prev => [...prev, option.id]);
        }
        setLocationSearchTerm('');
        setIsLocationOpen(false);
    };

    const handleLocationInputChange = (e) => {
        setLocationSearchTerm(e.target.value);
        setIsLocationOpen(true);
    };

    const handleLocationFocus = () => {
        if (locationCategoryFilter || locationsList?.length > 0) {
            updateDropdownPosition(locationInputRef, setLocationDropdownPos);
            setIsLocationOpen(true);
        }
    };

    const totalQuantity = isWater
        ? parseFloat(quantity[WATER_QUANTITY_KEY] || 0)
        : Object.values(quantity).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0);

    return (
        <div ref={modalContentRef}>
            {/* Seção 1: Material */}
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>
                        <FiPackage size={20} />
                    </div>
                    <h3 className={styles.sectionTitle}>{t('pages.stock.entry.materialSection', 'Material')}</h3>
                    <span className={styles.sectionBadge}>{t('common.required', 'Obrigatório')}</span>
                </div>
                <div className={styles.formGrid}>
                    {!isEditing && (
                        <div className={styles.filterRow} style={{ display: 'flex', gap: '16px', marginBottom: '16px', gridColumn: 'span 2' }}>
                            <div className={styles.inputGroup} style={{ flex: 1 }}>
                                <label htmlFor="filterType">{t('pages.stock.entry.filterByType', 'Filtrar por Tipo')}</label>
                                <select
                                    id="filterType"
                                    value={filterProductTypeId}
                                    onChange={(e) => setFilterProductTypeId(e.target.value)}
                                    className={`${styles.input} ${styles.select}`}
                                    disabled={loading}
                                >
                                    <option value="">{t('pages.stock.entry.allTypes', 'Todos os Tipos')}</option>
                                    {productTypes?.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.inputGroup} style={{ flex: 1 }}>
                                <label htmlFor="filterCategory">{t('pages.stock.entry.filterByCategory', 'Filtrar por Categoria')}</label>
                                <select
                                    id="filterCategory"
                                    value={filterCategoryId}
                                    onChange={(e) => setFilterCategoryId(e.target.value)}
                                    className={`${styles.input} ${styles.select}`}
                                    disabled={loading}
                                >
                                    <option value="">{t('pages.stock.entry.allCategories', 'Todas as Categorias')}</option>
                                    {categories?.map(category => (
                                        <option key={category.id} value={category.id}>{category.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className={styles.inputGroup} style={{ gridColumn: 'span 2', position: 'relative' }}>
                        <label>
                            {t('pages.stock.entry.materialLabel', 'Matéria-Prima/Produto')} <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.searchableSelectWrapper} style={{ position: 'relative' }}>
                            <input
                                ref={materialInputRef}
                                type="text"
                                className={`${styles.input} ${styles.comboInput}`}
                                placeholder={t('pages.stock.entry.materialPlaceholder', 'Buscar por código ou nome...')}
                                value={isMaterialOpen ? materialSearchTerm : materialDisplayValue}
                                onChange={handleMaterialInputChange}
                                onFocus={handleMaterialFocus}
                                disabled={loading || isEditing}
                            />
                            <div
                                className={`${styles.dropdownArrow} ${isMaterialOpen ? styles.open : ''}`}
                                onClick={() => {
                                    if (!isEditing) {
                                        if (!isMaterialOpen) {
                                            updateDropdownPosition(materialInputRef, setMaterialDropdownPos);
                                        }
                                        setIsMaterialOpen(!isMaterialOpen);
                                    }
                                }}
                            >
                                {isMaterialOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                            </div>

                            {isMaterialOpen && !isEditing && materialDropdownPos.width > 0 && (
                                <div
                                    ref={materialDropdownRef}
                                    className={styles.optionsDropdownPortal}
                                    style={{
                                        position: 'fixed',
                                        top: materialDropdownPos.top,
                                        left: materialDropdownPos.left,
                                        width: materialDropdownPos.width,
                                        zIndex: 10001,
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        backgroundColor: 'var(--content-bg)',
                                        border: '2px solid var(--border-color)',
                                        borderRadius: 'var(--border-radius-md)',
                                        boxShadow: 'var(--shadow-lg)'
                                    }}
                                >
                                    {loading && <div className={styles.loadingOption}>{t('common.loading', 'Carregando opções...')}</div>}
                                    {!loading && filteredRawMaterials.length > 0 ? (
                                        filteredRawMaterials.map(option => {
                                            const isWaterItem = isWaterMaterial(option);
                                            const isProductItemOption = isProductMaterial(option);
                                            return (
                                                <div
                                                    key={option.id}
                                                    className={`${styles.optionItem} ${selectedRawMaterialId === option.id ? styles.selected : ''}`}
                                                    onClick={() => handleSelectMaterial(option)}
                                                    style={{
                                                        padding: '10px 12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        borderBottom: '1px solid var(--border-color)',
                                                        transition: 'var(--transition-default)'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--row-hover-bg)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                >
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span className={styles.optionCode}>{option.code}</span>
                                                            <span style={{
                                                                fontSize: '10px',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                backgroundColor: isProductItemOption ? '#003a71' : '#0087b0',
                                                                color: '#ffffff',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {isProductItemOption ? t('pages.stock.entry.product', 'PRODUTO') : t('pages.stock.entry.rawMaterial', 'MATÉRIA-PRIMA')}
                                                            </span>
                                                            {isWaterItem && (
                                                                <span style={{
                                                                    fontSize: '10px',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: '#00bcd4',
                                                                    color: '#ffffff',
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    <FaWater size={10} style={{ marginRight: '4px' }} />
                                                                    {t('pages.stock.entry.infinite', '(Infinito)')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className={styles.optionName}>{option.commercialName}</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : !loading && (
                                        <div className={styles.noResults} style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                            {materialSearchTerm || filterProductTypeId || filterCategoryId
                                                ? t('pages.stock.entry.noResults', 'Nenhum resultado encontrado para os filtros aplicados.')
                                                : t('pages.stock.entry.startTyping', 'Comece a digitar para buscar.')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {!selectedRawMaterialId && !isEditing && (
                            <small className={styles.errorTextSmall}>{t('pages.stock.entry.materialRequired', 'Seleção é obrigatória.')}</small>
                        )}
                    </div>
                </div>
            </div>

            {/* Seção 2: Rastreabilidade */}
            {!isWater && (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>
                            <FiTag size={20} />
                        </div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.entry.traceabilitySection', 'Rastreabilidade')}</h3>
                        <span className={styles.sectionBadge}>
                            {isProductItem ? t('pages.stock.entry.lotRequired', 'Lote Obrigatório') : t('pages.stock.entry.optional', 'Opcional')}
                        </span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>
                                {t('pages.stock.entry.lotNumber', 'Número do Lote')}
                                {isProductItem && <span className={styles.required}>*</span>}
                            </label>
                            <input
                                type="text"
                                value={lotNumber}
                                onChange={(e) => setLotNumber(e.target.value.toUpperCase())}
                                className={styles.input}
                                placeholder={t('pages.stock.entry.lotPlaceholder', 'Ex: LOTE001, 2024-001')}
                                disabled={loading || isEditing}
                                style={{ textTransform: 'uppercase' }}
                                required={isProductItem}
                            />
                            {isProductItem && !lotNumber && !isEditing && (
                                <small className={styles.errorTextSmall}>{t('pages.stock.entry.lotRequiredMessage', 'Lote é obrigatório para produtos')}</small>
                            )}
                            {!isProductItem && (
                                <small className={styles.fieldHint}>
                                    {t('pages.stock.entry.lotOptionalHint', 'Número de identificação do lote (opcional para matérias-primas)')}
                                </small>
                            )}
                            {isProductItem && (
                                <small className={styles.fieldHint}>
                                    {t('pages.stock.entry.lotProductHint', 'Número de identificação do lote de fabricação')}
                                </small>
                            )}
                        </div>

                        <div className={styles.inputGroup}>
                            <label>{t('pages.stock.entry.manufacturerDate', 'Data de Fabricação')}</label>
                            <input
                                type="date"
                                value={manufacturerDate}
                                onChange={(e) => setManufacturerDate(e.target.value)}
                                className={styles.input}
                                disabled={loading || isEditing}
                            />
                            <small className={styles.fieldHint}>
                                {t('pages.stock.entry.manufacturerDateHint', 'Data em que o lote foi fabricado (opcional)')}
                            </small>
                        </div>

                        <div className={styles.inputGroup}>
                            <label>{t('pages.stock.entry.expirationDate', 'Data de Validade')}</label>
                            <input
                                type="date"
                                value={expirationDate}
                                onChange={(e) => setExpirationDate(e.target.value)}
                                className={styles.input}
                                disabled={loading || isEditing}
                            />
                            <small className={styles.fieldHint}>
                                {t('pages.stock.entry.expirationDateHint', 'Data limite para uso (opcional)')}
                            </small>
                        </div>
                    </div>
                </div>
            )}

            {/* Seção 3: Localização e Quantidade */}
            {isWater ? (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>
                            <FaWater size={20} />
                        </div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.entry.waterSection', 'Recurso Hídrico')}</h3>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.infoBox} style={{ gridColumn: 'span 2', margin: '0 0 16px 0' }}>
                            <span className={styles.infoIcon}>
                                <FiInfo size={20} />
                            </span>
                            <div>
                                <strong>{t('pages.stock.entry.waterMaterial', 'Material de Água (Recurso Infinito)')}</strong>
                                <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                                    {t('pages.stock.entry.waterDescription', 'Este material é considerado um recurso infinito/ubíquo no sistema. Apenas registre a quantidade utilizada - não é necessário selecionar localização e não afeta o estoque físico.')}
                                </p>
                            </div>
                        </div>

                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            <label>{t('pages.stock.entry.quantity', 'Quantidade')} <span className={styles.required}>*</span></label>
                            <div className={styles.quantityInputWrapper} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input
                                    type="number"
                                    step="0.001"
                                    min="0.001"
                                    name="waterQuantity"
                                    placeholder={t('pages.stock.entry.waterQuantityPlaceholder', 'Quantidade de água')}
                                    className={styles.input}
                                    style={{ flex: 1 }}
                                    value={quantity[WATER_QUANTITY_KEY] || ''}
                                    onChange={(e) => setQuantity({ [WATER_QUANTITY_KEY]: e.target.value })}
                                    required
                                    disabled={loading}
                                />
                                <span className={styles.unitText}>{t('pages.stock.entry.liters', 'Litros (L)')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>
                            <FiMapPin size={20} />
                        </div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.entry.locationSection', 'Localização e Quantidade')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required', 'Obrigatório')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="locationCategoryFilter">{t('pages.stock.entry.locationCategory', 'Categoria da Localização')}</label>
                            <select
                                id="locationCategoryFilter"
                                value={locationCategoryFilter}
                                onChange={(e) => {
                                    setLocationCategoryFilter(e.target.value);
                                    setLocationSearchTerm('');
                                    if (!isEditing) {
                                        setSelectedLocationIds([]);
                                        setQuantity({});
                                    }
                                }}
                                className={`${styles.input} ${styles.select}`}
                                disabled={loading || isEditing}
                            >
                                <option value="">{t('pages.stock.entry.selectCategory', 'Selecione uma categoria...')}</option>
                                <option value="mp_semi">{t('pages.stock.entry.categoryMp', 'MP e Semi Acabado')}</option>
                                <option value="embalagem_acabado">{t('pages.stock.entry.categoryPackaging', 'Embalagem e Produto Acabado')}</option>
                            </select>
                            <small className={styles.fieldHint}>
                                {t('pages.stock.entry.locationCategoryHint', 'Defina a categoria para filtrar as localizações disponíveis')}
                            </small>
                        </div>

                        <div className={styles.inputGroup} style={{ position: 'relative' }}>
                            <label>{t('pages.stock.entry.locations', 'Localização(ões)')} <span className={styles.required}>*</span></label>
                            <div className={styles.searchableSelectWrapper} style={{ position: 'relative' }}>
                                <input
                                    ref={locationInputRef}
                                    type="text"
                                    className={`${styles.input} ${styles.comboInput}`}
                                    placeholder={locationCategoryFilter ? t('pages.stock.entry.locationSearchPlaceholder', 'Buscar localização por ID ou descrição...') : t('pages.stock.entry.locationSelectCategoryFirst', 'Selecione uma categoria primeiro')}
                                    value={locationSearchTerm}
                                    onChange={handleLocationInputChange}
                                    onFocus={handleLocationFocus}
                                    disabled={loading || !locationCategoryFilter || isEditing}
                                />
                                <div
                                    className={`${styles.dropdownArrow} ${isLocationOpen ? styles.open : ''}`}
                                    onClick={() => {
                                        if (!isEditing && locationCategoryFilter) {
                                            if (!isLocationOpen) {
                                                updateDropdownPosition(locationInputRef, setLocationDropdownPos);
                                            }
                                            setIsLocationOpen(!isLocationOpen);
                                        }
                                    }}
                                >
                                    {isLocationOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                </div>

                                {isLocationOpen && locationCategoryFilter && locationDropdownPos.width > 0 && (
                                    <div
                                        ref={locationDropdownRef}
                                        className={styles.optionsDropdownPortal}
                                        style={{
                                            position: 'fixed',
                                            top: locationDropdownPos.top,
                                            left: locationDropdownPos.left,
                                            width: locationDropdownPos.width,
                                            zIndex: 10001,
                                            maxHeight: '250px',
                                            overflowY: 'auto',
                                            backgroundColor: 'var(--content-bg)',
                                            border: '2px solid var(--border-color)',
                                            borderRadius: 'var(--border-radius-md)',
                                            boxShadow: 'var(--shadow-lg)'
                                        }}
                                    >
                                        {filteredLocations.length > 0 ? (
                                            filteredLocations.map(option => {
                                                const isSelected = selectedLocationIds.includes(option.id);
                                                return (
                                                    <div
                                                        key={option.id}
                                                        className={`${styles.optionItem} ${isSelected ? styles.selected : ''}`}
                                                        onClick={() => handleSelectLocation(option)}
                                                        style={{
                                                            padding: '10px 12px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            borderBottom: '1px solid var(--border-color)',
                                                            backgroundColor: isSelected ? 'var(--primary-light)' : 'transparent'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--row-hover-bg)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span className={styles.optionCode}>{option.id}</span>
                                                                {option.categoryName && (
                                                                    <span style={{ fontSize: '10px', color: '#666' }}>
                                                                        ({option.categoryName})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {option.description && (
                                                                <span style={{ fontSize: '11px', color: '#888' }}>{option.description}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className={styles.noResults} style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                                {locationSearchTerm
                                                    ? t('pages.stock.entry.noLocationFound', 'Nenhuma localização encontrada para "{term}"', { term: locationSearchTerm })
                                                    : t('pages.stock.entry.noLocationAvailable', 'Nenhuma localização disponível nesta categoria')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <small className={styles.fieldHint}>
                                {t('pages.stock.entry.locationHint', 'Selecione uma ou mais localizações para armazenar o material')}
                            </small>
                        </div>
                    </div>

                    {selectedLocationIds.length > 0 && (
                        <div className={styles.quantitySection}>
                            <label className={styles.sectionLabel}>{t('pages.stock.entry.quantitiesByLocation', 'Quantidades por Localização')}</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                                {selectedLocationIds.map(locId => {
                                    const location = locationsList?.find(l => l.id === locId);
                                    return (
                                        <div key={locId} className={styles.quantityRow}>
                                            <div style={{ minWidth: '140px' }}>
                                                <strong>{locId}</strong>
                                                {location?.categoryName && (
                                                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                                        {location.categoryName}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input
                                                    type="number"
                                                    step="0.001"
                                                    min="0.001"
                                                    placeholder={t('pages.stock.entry.quantityPlaceholder', 'Quantidade')}
                                                    className={styles.input}
                                                    style={{ flex: 1 }}
                                                    value={quantity[locId] || ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setQuantity(prev => ({ ...prev, [locId]: val }));
                                                    }}
                                                    required
                                                    disabled={loading}
                                                />
                                                <span className={styles.unitText}>{t('pages.stock.entry.unitKg', 'kg/L/un')}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className={styles.totalQuantityRow}>
                                    <span>{t('pages.stock.entry.total', 'Total:')} </span>
                                    <span style={{ color: 'var(--primary-color)', fontSize: '1.1rem' }}>
                                        {totalQuantity.toFixed(3)} {t('pages.stock.entry.unitKg', 'kg/L/un')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Seção 4: Observações */}
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>
                        <FiClipboard size={20} />
                    </div>
                    <h3 className={styles.sectionTitle}>{t('pages.stock.entry.notesSection', 'Observações')}</h3>
                    <span className={styles.sectionBadge}>{t('pages.stock.entry.optionalBadge', 'Opcional')}</span>
                </div>
                <div className={styles.formGrid}>
                    <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className={styles.textarea}
                            rows="3"
                            disabled={loading}
                            placeholder={isEditing
                                ? t('pages.stock.entry.editNotesPlaceholder', 'Descreva o motivo da alteração da quantidade...')
                                : t('pages.stock.entry.notesPlaceholder', 'Adicione notas sobre a entrada (Fornecedor, Informações adicionais, etc.)')}
                        />
                        <small className={styles.fieldHint}>
                            {isEditing ? t('pages.stock.entry.editNotesHint', 'Justificativa para edição da quantidade') : t('pages.stock.entry.notesHint', 'Informações complementares sobre esta entrada')}
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Componente Principal ---
const StockEntryPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('entrada_de_estoque');
    const [rawMaterials, setRawMaterials] = useState([]);
    const [locations, setLocations] = useState([]);
    const [categories, setCategories] = useState([]);
    const [stockEntries, setStockEntries] = useState([]);
    const [entryToEdit, setEntryToEdit] = useState(null);
    const [selectedRawMaterialId, setSelectedRawMaterialId] = useState('');
    const [selectedLocationIds, setSelectedLocationIds] = useState([]);
    const [quantity, setQuantity] = useState({});
    const [notes, setNotes] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingEntries, setLoadingEntries] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [lastDoc, setLastDoc] = useState(null);
    const [status, setStatus] = useState({ message: '', type: '' });
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [locationCategoryFilter, setLocationCategoryFilter] = useState('');
    const [productTypes, setProductTypes] = useState([]);
    const [filterProductTypeId, setFilterProductTypeId] = useState('');
    const [filterCategoryId, setFilterCategoryId] = useState('');
    const [lotNumber, setLotNumber] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [manufacturerDate, setManufacturerDate] = useState('');

    // Delete confirm modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Import-related state
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importCategory, setImportCategory] = useState('');
    const [isImporting, setIsImporting] = useState(false);

    const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);
    const staticDataLoaded = useRef(false);
    const observerRef = useRef();

    const { currentUser } = useAuth();
    const USER_EMAIL = currentUser?.email || 'sistema';

    const entriesCollectionRef = useMemo(() => collection(db, STOCK_ENTRIES_COLLECTION), []);
    const currentStockCollectionRef = useMemo(() => collection(db, CURRENT_STOCK_COLLECTION), []);
    const rawMaterialsCollectionRef = useMemo(() => collection(db, RAW_MATERIALS_COLLECTION), []);
    const productsCollectionRef = useMemo(() => collection(db, PRODUCTS_COLLECTION), []);
    const locationsCollectionRef = useMemo(() => collection(db, LOCATIONS_COLLECTION), []);
    const productTypesCollectionRef = useMemo(() => collection(db, PRODUCT_TYPE_COLLECTION_PATH), []);
    const categoriesCollectionRef = useMemo(() => collection(db, CATEGORY_COLLECTION_PATH), []);

    const checkRawMaterialExists = useCallback(async (transaction, rawMaterialId) => {
        const mpRef = doc(rawMaterialsCollectionRef, rawMaterialId);
        const productRef = doc(productsCollectionRef, rawMaterialId);
        const mpDoc = await transaction.get(mpRef);
        const productDoc = await transaction.get(productRef);
        if (!mpDoc.exists() && !productDoc.exists()) {
            throw new Error(t('pages.stock.entry.materialNotFoundError', 'Matéria-prima/produto com ID {id} não existe mais no sistema.', { id: rawMaterialId }));
        }
        return true;
    }, [rawMaterialsCollectionRef, productsCollectionRef, t]);

    const fetchStaticData = useCallback(async () => {
        if (staticDataLoaded.current && rawMaterials.length > 0 && locations.length > 0) {
            return;
        }

        const cacheKey = 'stock-entry-static-data';
        const cachedData = cache.get(cacheKey);

        if (cachedData) {
            setRawMaterials(cachedData.rawMaterials);
            setLocations(cachedData.locations);
            setProductTypes(cachedData.productTypes);
            setCategories(cachedData.categories);
            staticDataLoaded.current = true;
            return;
        }

        setLoading(true);
        try {
            const [mpData, productData, locData, typesData, catData] = await Promise.all([
                getDocs(rawMaterialsCollectionRef),
                getDocs(productsCollectionRef),
                getDocs(locationsCollectionRef),
                getDocs(productTypesCollectionRef),
                getDocs(categoriesCollectionRef)
            ]);

            const fetchedRawMaterials = mpData.docs.map(doc => ({ id: doc.id, ...doc.data(), isProduct: false }));
            const fetchedProducts = productData.docs.map(doc => ({ id: doc.id, ...doc.data(), isProduct: true }));
            const combinedList = [...fetchedRawMaterials, ...fetchedProducts];
            combinedList.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
            setRawMaterials(combinedList);

            const fetchedLocations = locData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedLocations.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
            setLocations(fetchedLocations);

            const fetchedTypes = typesData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProductTypes(fetchedTypes);

            const fetchedCategories = catData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCategories(fetchedCategories);

            cache.set(cacheKey, {
                rawMaterials: combinedList,
                locations: fetchedLocations,
                productTypes: fetchedTypes,
                categories: fetchedCategories
            });

            staticDataLoaded.current = true;
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            setStatus({ message: t('pages.stock.entry.loadError', 'Erro ao carregar dados. Recarregue a página.'), type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [rawMaterialsCollectionRef, productsCollectionRef, locationsCollectionRef, productTypesCollectionRef, categoriesCollectionRef, rawMaterials.length, locations.length, t]);

    const loadEntries = useCallback(async (isLoadMore = false) => {
        if (loadingEntries) return;
        setLoadingEntries(true);
        try {
            let q = query(entriesCollectionRef, orderBy('createdAt', 'desc'), limit(ITEMS_PER_PAGE));
            if (isLoadMore && lastDoc) {
                q = query(q, startAfter(lastDoc));
            }
            const snapshot = await getDocs(q);
            const newEntries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (isLoadMore) {
                setStockEntries(prev => [...prev, ...newEntries]);
            } else {
                setStockEntries(newEntries);
            }

            setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
            setHasMore(snapshot.docs.length === ITEMS_PER_PAGE);

            if (!isLoadMore && totalCount === 0) {
                const totalSnapshot = await getCountFromServer(entriesCollectionRef);
                setTotalCount(totalSnapshot.data().count);
            }
        } catch (error) {
            console.error("Erro ao carregar entradas:", error);
            setStatus({ message: t('pages.stock.entry.historyLoadError', 'Erro ao carregar histórico.'), type: 'error' });
        } finally {
            setLoadingEntries(false);
        }
    }, [entriesCollectionRef, lastDoc, loadingEntries, totalCount, t]);

    useEffect(() => {
        loadEntries(false);
    }, []);

    const lastElementRef = useCallback(node => {
        if (loadingEntries) return;
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadEntries(true);
            }
        });
        if (node) observerRef.current.observe(node);
    }, [loadingEntries, hasMore, loadEntries]);

    useEffect(() => {
        fetchStaticData();
    }, [fetchStaticData]);

    const handleResetForm = () => {
        setSelectedRawMaterialId('');
        setSelectedLocationIds([]);
        setQuantity({});
        setNotes('');
        setEntryToEdit(null);
        setFilterProductTypeId('');
        setFilterCategoryId('');
        setLocationCategoryFilter('');
        setLotNumber('');
        setExpirationDate('');
        setManufacturerDate('');
        setStatus({ message: '', type: '' });
    };

    const handleCloseModal = () => {
        handleResetForm();
        setIsModalOpen(false);
    };

    const handleAddClick = async () => {
        if (!staticDataLoaded.current || rawMaterials.length === 0 || locations.length === 0) {
            setStatus({ message: t('pages.stock.entry.loadingData', 'Carregando dados, aguarde...'), type: 'info' });
            await fetchStaticData();
            if (rawMaterials.length === 0) {
                setStatus({ message: t('pages.stock.entry.loadError', 'Erro ao carregar dados. Recarregue a página.'), type: 'error' });
                return;
            }
        }
        handleResetForm();
        setIsModalOpen(true);
        setStatus({ message: '', type: '' });
    };

    const handleProcessImport = async () => {
        if (!importFile || !importCategory) return;

        setIsImporting(true);
        setStatus({ message: t('pages.stock.import.readingFile', 'Lendo o arquivo de estoque...'), type: 'info' });

        try {
            const data = await importFile.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array', cellDates: true });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

            if (rawRows.length === 0) {
                throw new Error(t('pages.stock.import.emptyFile', 'A planilha está vazia.'));
            }

            const headers = Object.keys(rawRows[0]);

            const findColumn = (possibleNames) => {
                for (const name of possibleNames) {
                    const found = headers.find(h => h.toLowerCase().trim() === name.toLowerCase().trim());
                    if (found) return found;
                }
                return null;
            };

            const colCodigo = findColumn(['Código', 'CODIGO', 'code', 'Code']);
            const colLocalizacao = findColumn(['Localização', 'LOCALIZAÇÃO', 'Localizacao', 'location']);
            const colQuantidade = findColumn(['Quantidade', 'PESO (KG)', 'Peso', 'quantity', 'Qtd']);
            const colLote = findColumn(['Lote', 'LOTE', 'lot']);
            const colFabricado = findColumn(['Fabricado', 'FABRICAÇÃO', 'Fabricacao', 'manufacturer']);
            const colValidade = findColumn(['Validade', 'VALIDADE', 'expiration']);

            if (!colCodigo || !colLocalizacao || !colQuantidade) {
                throw new Error(t('pages.stock.import.missingColumns', 'Colunas necessárias não encontradas. Cabeçalhos disponíveis: {headers}', { headers: headers.join(', ') }));
            }

            setStatus({ message: t('pages.stock.import.processingRows', 'Processando {count} registros...', { count: rawRows.length }), type: 'info' });

            const processedKeys = new Set();
            let successCount = 0;
            let skippedCount = 0;
            let errorCount = 0;
            let materialNotFound = 0;
            let locationNotFound = 0;

            const formatDateLocal = (rawDate) => {
                if (!rawDate) return null;
                if (rawDate instanceof Date) {
                    const year = rawDate.getFullYear();
                    const month = String(rawDate.getMonth() + 1).padStart(2, '0');
                    const day = String(rawDate.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
                if (typeof rawDate === 'string') {
                    const parsed = new Date(rawDate);
                    if (!isNaN(parsed.getTime())) {
                        const year = parsed.getFullYear();
                        const month = String(parsed.getMonth() + 1).padStart(2, '0');
                        const day = String(parsed.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                    }
                    if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
                        return rawDate;
                    }
                    const parts = rawDate.split(/[\/\-.]/);
                    if (parts.length === 3) {
                        const day = String(parseInt(parts[0])).padStart(2, '0');
                        const month = String(parseInt(parts[1])).padStart(2, '0');
                        const year = parts[2];
                        if (year.length === 4 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                            return `${year}-${month}-${day}`;
                        }
                    }
                }
                return null;
            };

            const extractQuantity = (value) => {
                if (typeof value === 'number') return value;
                if (typeof value === 'string') {
                    const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
                    const parsed = parseFloat(cleaned);
                    if (!isNaN(parsed) && parsed > 0) return parsed;
                }
                return null;
            };

            const extractLocation = (value) => {
                if (!value) return null;
                const str = value.toString().trim().toUpperCase();
                const patterns = [
                    /(PRATELEIRA\s*[A-Z0-9\-]+)/i,
                    /(P[0-9]+)/i,
                    /(ÁREA\s*[0-9]+)/i,
                    /(AREA\s*[0-9]+)/i,
                    /(SEM ESTOQUE)/i,
                    /(LABORATORIO)/i,
                    /(ARMÁRIO\s*[A-Z]+)/i
                ];

                for (const pattern of patterns) {
                    const match = str.match(pattern);
                    if (match) {
                        const location = match[1].toUpperCase().replace(/ÁREA/g, 'ÁREA').replace(/AREA/g, 'ÁREA');
                        return location;
                    }
                }

                const cleaned = str.replace(/[\/,;]/g, ' ').trim().split(/\s+/)[0];
                return cleaned || null;
            };

            for (const row of rawRows) {
                try {
                    const materialCode = row[colCodigo]?.toString().trim().toUpperCase();
                    const locationRaw = row[colLocalizacao]?.toString().trim();
                    const qty = extractQuantity(row[colQuantidade]);
                    const lot = row[colLote]?.toString().trim().toUpperCase() || '';
                    const fabricadoRaw = row[colFabricado];
                    const validadeRaw = row[colValidade];

                    if (!materialCode) {
                        skippedCount++;
                        continue;
                    }

                    const matchedMaterial = rawMaterials.find(m =>
                        m.code?.toUpperCase() === materialCode ||
                        m.id?.toUpperCase() === materialCode
                    );

                    if (!matchedMaterial) {
                        materialNotFound++;
                        skippedCount++;
                        continue;
                    }

                    if (!qty || qty <= 0) {
                        skippedCount++;
                        continue;
                    }

                    const locationId = extractLocation(locationRaw);
                    if (!locationId) {
                        locationNotFound++;
                        skippedCount++;
                        continue;
                    }

                    const matchedLocation = locations.find(l =>
                        l.id?.toUpperCase() === locationId ||
                        l.description?.toUpperCase().includes(locationId)
                    );

                    if (!matchedLocation) {
                        locationNotFound++;
                        skippedCount++;
                        continue;
                    }

                    if (matchedLocation.category !== importCategory) {
                        skippedCount++;
                        continue;
                    }

                    const isProductItem = matchedMaterial.isProduct === true;

                    let entryKey;
                    if (lot) {
                        entryKey = `${matchedMaterial.id}_${matchedLocation.id}_${lot}`;
                    } else {
                        entryKey = `${matchedMaterial.id}_${matchedLocation.id}`;
                    }

                    if (processedKeys.has(entryKey)) {
                        skippedCount++;
                        continue;
                    }

                    let existingStockId;
                    if (lot) {
                        existingStockId = `${matchedMaterial.id}_${matchedLocation.id}_${lot}`;
                    } else {
                        existingStockId = `${matchedMaterial.id}_${matchedLocation.id}`;
                    }

                    const existingStockRef = doc(currentStockCollectionRef, existingStockId);
                    const existingStockDoc = await getDoc(existingStockRef);

                    if (existingStockDoc.exists()) {
                        const existingData = existingStockDoc.data();
                        const existingQty = existingData.quantity || 0;
                        if (Math.abs(existingQty - qty) < 0.001) {
                            skippedCount++;
                            continue;
                        }
                    }

                    processedKeys.add(entryKey);

                    const manufacturerDateStr = formatDateLocal(fabricadoRaw);
                    const expirationDateStr = formatDateLocal(validadeRaw);

                    const entryData = {
                        rawMaterialId: matchedMaterial.id,
                        rawMaterialCode: matchedMaterial.code,
                        rawMaterialName: matchedMaterial.commercialName,
                        locationIds: [matchedLocation.id],
                        locationQuantities: { [matchedLocation.id]: qty },
                        quantity: qty,
                        isWater: false,
                        isProduct: isProductItem,
                        type: 'ENTRADA',
                        notes: t('pages.stock.import.importNote', 'Importação de Planilha ({category})', { category: importCategory }),
                        createdAt: serverTimestamp(),
                        createdBy: USER_EMAIL
                    };

                    if (lot) entryData.lotNumber = lot;
                    if (expirationDateStr) entryData.expirationDate = expirationDateStr;
                    if (manufacturerDateStr) entryData.manufacturerDate = manufacturerDateStr;

                    await runTransaction(db, async (transaction) => {
                        const currentStockRef = doc(currentStockCollectionRef, existingStockId);
                        const stockDoc = await transaction.get(currentStockRef);

                        const currentQuantity = stockDoc.exists() ? (stockDoc.data().quantity || 0) : 0;
                        const newQuantity = currentQuantity + qty;

                        const stockData = {
                            rawMaterialId: matchedMaterial.id,
                            locationId: matchedLocation.id,
                            quantity: newQuantity,
                            rawMaterialCode: matchedMaterial.code,
                            rawMaterialName: matchedMaterial.commercialName,
                            locationDescription: matchedLocation.description || matchedLocation.id,
                            updatedAt: serverTimestamp(),
                            createdAt: stockDoc.exists() ? stockDoc.data().createdAt : serverTimestamp(),
                        };

                        if (lot) {
                            stockData.lotNumber = lot;
                            stockData.expirationDate = expirationDateStr;
                            stockData.manufacturerDate = manufacturerDateStr;
                            stockData.isProduct = isProductItem;
                        }

                        if (isProductItem && lot) {
                            stockData.originalStockId = `${matchedMaterial.id}_${matchedLocation.id}`;
                        }

                        const newEntryRef = doc(entriesCollectionRef);
                        transaction.set(newEntryRef, entryData);
                        transaction.set(currentStockRef, stockData);
                    });

                    successCount++;

                } catch (rowError) {
                    console.error("Erro ao processar linha:", rowError);
                    errorCount++;
                }
            }

            setStatus({
                message: t('pages.stock.import.importComplete', 'Importação concluída! {success} registros adicionados, {skipped} ignorados ({materialNotFound} materiais não encontrados, {locationNotFound} localizações não encontradas, {errorCount} erros).', {
                    success: successCount,
                    skipped: skippedCount,
                    materialNotFound,
                    locationNotFound,
                    errorCount
                }),
                type: successCount > 0 ? 'success' : 'warning'
            });

            if (successCount > 0) {
                setIsImportModalOpen(false);
                setImportFile(null);
                setImportCategory('');
                loadEntries(false);
            }

        } catch (error) {
            console.error(error);
            setStatus({ message: t('pages.stock.import.importError', 'Erro na importação: {message}', { message: error.message }), type: 'error' });
        } finally {
            setIsImporting(false);
        }
    };

    const handleEditClick = (entry) => {
        setEntryToEdit(entry);
        setSelectedRawMaterialId(entry.rawMaterialId);
        setSelectedLocationIds(entry.locationIds || []);
        setLocationCategoryFilter('');
        setLotNumber(entry.lotNumber || '');
        setExpirationDate(entry.expirationDate || '');
        setManufacturerDate(entry.manufacturerDate || '');

        if (entry.locationQuantities && typeof entry.locationQuantities === 'object') {
            setQuantity(entry.locationQuantities);
        } else {
            const qtyObj = {};
            (entry.locationIds || []).forEach(locId => {
                qtyObj[locId] = (entry.quantity / (entry.locationIds?.length || 1)).toFixed(3);
            });
            setQuantity(qtyObj);
        }

        setNotes(entry.notes || '');
        setFilterProductTypeId('');
        setFilterCategoryId('');
        setIsModalOpen(true);
    };

    const handleDeleteClick = (entry) => {
        setDeleteTarget(entry);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        const entry = deleteTarget;
        if (!entry) return;

        setIsDeleting(true);
        try {
            await handleDelete(entry);
            setDeleteModalOpen(false);
            setIsDeleting(false);
        } catch (error) {
            setIsDeleting(false);
        }
    };

    const handleSave = (entryId) => {
        if (entryId) {
            handleUpdate(entryId);
        } else {
            handleCreate();
        }
    };

    const handleCreate = async () => {
        setLoading(true);
        setStatus({ message: '', type: '' });

        const rmData = rawMaterials.find(r => r.id === selectedRawMaterialId);
        const isWater = rmData ? isWaterMaterial(rmData) : false;
        const isProductItem = rmData ? isProductMaterial(rmData) : false;

        let totalQuantity;
        let locationQuantities = {};

        if (isWater) {
            totalQuantity = parseFloat(quantity[WATER_QUANTITY_KEY] || 0);
            locationQuantities = { [WATER_QUANTITY_KEY]: totalQuantity };
        } else {
            const hasInvalidQty = selectedLocationIds.some(id => !quantity[id] || parseFloat(quantity[id]) <= 0);
            totalQuantity = selectedLocationIds.reduce((acc, id) => acc + (parseFloat(quantity[id]) || 0), 0);
            if (selectedLocationIds.length === 0 || hasInvalidQty) {
                setStatus({ message: t('pages.stock.entry.quantityRequired', 'Informe as quantidades para todas as localizações selecionadas.'), type: 'error' });
                setLoading(false);
                return;
            }
            locationQuantities = quantity;
        }

        if (!rmData || totalQuantity <= 0) {
            setStatus({ message: t('pages.stock.entry.materialRequired', 'Informe a Matéria-Prima/Produto e a quantidade válida.'), type: 'error' });
            setLoading(false);
            return;
        }

        if (isProductItem && !isWater && !lotNumber.trim()) {
            setStatus({ message: t('pages.stock.entry.lotRequiredMessage', 'Número do Lote é obrigatório para produtos.'), type: 'error' });
            setLoading(false);
            return;
        }

        try {
            await runTransaction(db, async (transaction) => {
                await checkRawMaterialExists(transaction, selectedRawMaterialId);
                const stockDataToUpdate = [];

                if (!isWater) {
                    for (const locationId of selectedLocationIds) {
                        const qtyForThisLoc = parseFloat(quantity[locationId]);

                        let currentStockId;
                        if (lotNumber.trim()) {
                            currentStockId = `${selectedRawMaterialId}_${locationId}_${lotNumber.trim()}`;
                        } else {
                            currentStockId = `${selectedRawMaterialId}_${locationId}`;
                        }

                        const currentStockRef = doc(currentStockCollectionRef, currentStockId);
                        const stockDoc = await transaction.get(currentStockRef);
                        const currentQuantity = stockDoc.exists() ? (stockDoc.data().quantity || 0) : 0;
                        const newQuantity = currentQuantity + qtyForThisLoc;

                        const stockData = {
                            rawMaterialId: selectedRawMaterialId,
                            locationId: locationId,
                            quantity: newQuantity,
                            rawMaterialCode: rmData.code,
                            rawMaterialName: rmData.commercialName,
                            locationDescription: locations.find(l => l.id === locationId)?.description || locationId,
                            updatedAt: serverTimestamp(),
                            createdAt: stockDoc.exists() ? stockDoc.data().createdAt : serverTimestamp(),
                        };

                        if (lotNumber.trim()) {
                            stockData.lotNumber = lotNumber.trim();
                            stockData.expirationDate = expirationDate || null;
                            stockData.manufacturerDate = manufacturerDate || null;
                            stockData.isProduct = isProductItem;
                        }

                        if (isProductItem && lotNumber.trim()) {
                            stockData.originalStockId = `${selectedRawMaterialId}_${locationId}`;
                        }

                        stockDataToUpdate.push({
                            ref: currentStockRef,
                            data: stockData
                        });
                    }
                }

                const newEntryRef = doc(entriesCollectionRef);
                const entryData = {
                    rawMaterialId: selectedRawMaterialId,
                    rawMaterialCode: rmData.code,
                    rawMaterialName: rmData.commercialName,
                    locationIds: isWater ? [] : selectedLocationIds,
                    locationQuantities: locationQuantities,
                    quantity: totalQuantity,
                    isWater: isWater,
                    isProduct: isProductItem && !isWater,
                    type: 'ENTRADA',
                    notes: notes.trim(),
                    createdAt: serverTimestamp(),
                    createdBy: USER_EMAIL
                };

                if (lotNumber.trim()) {
                    entryData.lotNumber = lotNumber.trim();
                    entryData.expirationDate = expirationDate || null;
                    entryData.manufacturerDate = manufacturerDate || null;
                }

                transaction.set(newEntryRef, entryData);
                for (const item of stockDataToUpdate) {
                    transaction.set(item.ref, item.data);
                }
            });

            setStatus({ message: t('pages.stock.entry.createSuccess', 'Entrada registrada com sucesso!{lot}', { lot: lotNumber.trim() ? ` Lote: ${lotNumber.trim()}` : '' }), type: 'success' });
            handleCloseModal();
            setLastDoc(null);
            loadEntries(false);
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
        } catch (error) {
            console.error(error);
            setStatus({ message: t('pages.stock.entry.createError', 'Erro: {message}', { message: error.message }), type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (entryId) => {
        setLoading(true);
        setStatus({ message: '', type: '' });

        const rmData = rawMaterials.find(r => r.id === selectedRawMaterialId);
        const oldEntry = entryToEdit;
        const isWater = oldEntry?.isWater || (rmData && isWaterMaterial(rmData));
        const isProductItem = oldEntry?.isProduct || (rmData && isProductMaterial(rmData));

        let newTotalQuantity;
        let locationQuantities;

        if (isWater) {
            newTotalQuantity = parseFloat(quantity[WATER_QUANTITY_KEY] || 0);
            locationQuantities = { [WATER_QUANTITY_KEY]: newTotalQuantity };
        } else {
            newTotalQuantity = Object.values(quantity).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0);
            locationQuantities = quantity;
        }

        const oldTotalQuantity = parseFloat(oldEntry?.quantity || 0);
        const locationsToUpdate = oldEntry?.locationIds || [];
        const quantityChange = newTotalQuantity - oldTotalQuantity;

        if (!rmData || newTotalQuantity <= 0 || isNaN(newTotalQuantity)) {
            setStatus({ message: t('pages.stock.entry.invalidQuantity', 'A Quantidade deve ser válida (> 0).'), type: 'error' });
            setLoading(false);
            return;
        }

        if (quantityChange === 0 && notes.trim() === (oldEntry?.notes || '').trim()) {
            setStatus({ message: t('pages.stock.entry.noChanges', 'Nenhuma alteração detectada.'), type: 'info' });
            setLoading(false);
            handleCloseModal();
            return;
        }

        try {
            await runTransaction(db, async (transaction) => {
                await checkRawMaterialExists(transaction, selectedRawMaterialId);
                const stockDataToUpdate = [];

                if (!isWater && locationsToUpdate.length > 0) {
                    const changePerLocation = parseFloat((quantityChange / locationsToUpdate.length).toFixed(3));
                    for (const locationId of locationsToUpdate) {
                        let currentStockId;
                        const lotToUse = oldEntry.lotNumber || lotNumber;
                        if (lotToUse) {
                            currentStockId = `${oldEntry.rawMaterialId}_${locationId}_${lotToUse}`;
                        } else {
                            currentStockId = `${oldEntry.rawMaterialId}_${locationId}`;
                        }

                        const currentStockRef = doc(currentStockCollectionRef, currentStockId);
                        const stockDoc = await transaction.get(currentStockRef);
                        if (!stockDoc.exists()) {
                            throw new Error(t('pages.stock.entry.stockNotFound', 'Estoque atual para {location} não encontrado.', { location: locationId }));
                        }
                        const currentQuantity = stockDoc.data().quantity || 0;
                        const newQuantity = currentQuantity + changePerLocation;
                        if (newQuantity < 0) {
                            throw new Error(t('pages.stock.entry.negativeStock', 'A nova quantidade resulta em saldo negativo na localização {location}.', { location: locationId }));
                        }

                        const stockData = {
                            rawMaterialId: oldEntry.rawMaterialId,
                            locationId: locationId,
                            quantity: newQuantity,
                            rawMaterialCode: oldEntry.rawMaterialCode,
                            rawMaterialName: oldEntry.rawMaterialName,
                            locationDescription: locations.find(l => l.id === locationId)?.description || locationId,
                            updatedAt: serverTimestamp(),
                            createdAt: stockDoc.data().createdAt,
                        };

                        const lotToSave = oldEntry.lotNumber || lotNumber;
                        if (lotToSave) {
                            stockData.lotNumber = lotToSave;
                            stockData.expirationDate = oldEntry.expirationDate || expirationDate;
                            stockData.manufacturerDate = oldEntry.manufacturerDate || manufacturerDate;
                        }

                        stockDataToUpdate.push({
                            ref: currentStockRef,
                            data: stockData
                        });
                    }
                }

                const entryRef = doc(entriesCollectionRef, entryId);
                transaction.update(entryRef, {
                    quantity: newTotalQuantity,
                    locationQuantities: locationQuantities,
                    notes: notes.trim(),
                    updatedAt: serverTimestamp(),
                    updatedBy: USER_EMAIL
                });

                for (const item of stockDataToUpdate) {
                    if (item.data.quantity <= 0.001) {
                        transaction.delete(item.ref);
                    } else {
                        transaction.set(item.ref, item.data);
                    }
                }
            });

            setStatus({ message: t('pages.stock.entry.updateSuccess', 'Entrada atualizada com sucesso!'), type: 'success' });
            handleCloseModal();
            setLastDoc(null);
            loadEntries(false);
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
        } catch (error) {
            console.error("Erro ao atualizar:", error);
            setStatus({ message: t('pages.stock.entry.updateError', 'Erro: {message}', { message: error.message }), type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (entryToDelete) => {
        setLoading(true);
        setStatus({ message: '', type: '' });

        const quantityToDelete = parseFloat(entryToDelete.quantity);
        const locationsToUpdate = entryToDelete.locationIds || [];
        const isWater = entryToDelete.isWater;
        const isProductItem = entryToDelete.isProduct;
        const lotNumberToDelete = entryToDelete.lotNumber;

        try {
            await runTransaction(db, async (transaction) => {
                const stockDataToUpdate = [];

                if (!isWater && locationsToUpdate.length > 0) {
                    const quantityToRemovePerLocation = parseFloat((quantityToDelete / locationsToUpdate.length).toFixed(3));
                    for (const locationId of locationsToUpdate) {
                        let currentStockId;
                        if (lotNumberToDelete) {
                            currentStockId = `${entryToDelete.rawMaterialId}_${locationId}_${lotNumberToDelete}`;
                        } else {
                            currentStockId = `${entryToDelete.rawMaterialId}_${locationId}`;
                        }

                        const currentStockRef = doc(currentStockCollectionRef, currentStockId);
                        const stockDoc = await transaction.get(currentStockRef);
                        if (!stockDoc.exists()) {
                            throw new Error(t('pages.stock.entry.stockNotFound', 'Estoque para {location} não encontrado.', { location: locationId }));
                        }
                        const currentQuantity = stockDoc.data().quantity || 0;
                        const newQuantity = currentQuantity - quantityToRemovePerLocation;
                        if (newQuantity < 0) {
                            throw new Error(t('pages.stock.entry.deleteNegativeStock', 'Exclusão resulta em saldo negativo em {location}.', { location: locationId }));
                        }

                        const stockData = {
                            rawMaterialId: entryToDelete.rawMaterialId,
                            locationId: locationId,
                            quantity: newQuantity,
                            rawMaterialCode: entryToDelete.rawMaterialCode,
                            rawMaterialName: entryToDelete.rawMaterialName,
                            locationDescription: locations.find(l => l.id === locationId)?.description || locationId,
                            updatedAt: serverTimestamp(),
                            createdAt: stockDoc.data().createdAt,
                        };

                        if (lotNumberToDelete) {
                            stockData.lotNumber = lotNumberToDelete;
                            stockData.expirationDate = entryToDelete.expirationDate;
                            stockData.manufacturerDate = entryToDelete.manufacturerDate;
                        }

                        stockDataToUpdate.push({
                            ref: currentStockRef,
                            data: stockData
                        });
                    }
                }

                const entryRef = doc(entriesCollectionRef, entryToDelete.id);
                transaction.delete(entryRef);
                for (const item of stockDataToUpdate) {
                    if (item.data.quantity <= 0.001) {
                        transaction.delete(item.ref);
                    } else {
                        transaction.set(item.ref, item.data);
                    }
                }
            });

            setStatus({ message: t('pages.stock.entry.deleteSuccess', 'Entrada excluída com sucesso!'), type: 'success' });
            setLastDoc(null);
            loadEntries(false);
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
        } catch (error) {
            console.error("Erro ao excluir:", error);
            setStatus({ message: t('pages.stock.entry.deleteError', 'Erro: {message}', { message: error.message }), type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const filteredEntries = useMemo(() => {
        if (!stockEntries) return [];
        if (!debouncedSearchTerm) return stockEntries;
        const lowerCaseSearchTerm = debouncedSearchTerm.toLowerCase();
        return stockEntries.filter(item => {
            return (item.rawMaterialCode || '').toLowerCase().includes(lowerCaseSearchTerm) ||
                (item.rawMaterialName || '').toLowerCase().includes(lowerCaseSearchTerm) ||
                (item.notes || '').toLowerCase().includes(lowerCaseSearchTerm) ||
                (item.createdBy || '').toLowerCase().includes(lowerCaseSearchTerm) ||
                (item.updatedBy || '').toLowerCase().includes(lowerCaseSearchTerm) ||
                (item.lotNumber || '').toLowerCase().includes(lowerCaseSearchTerm);
        });
    }, [stockEntries, debouncedSearchTerm]);

    const totalFilteredItems = filteredEntries.length;
    const totalPages = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);
    const currentEntries = useMemo(() => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        return filteredEntries.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredEntries, page]);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) setPage(newPage);
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleString('pt-BR');
        } catch {
            return 'Data inválida';
        }
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return '—';
        try {
            const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
            return date.toLocaleDateString('pt-BR');
        } catch {
            return 'Inválida';
        }
    };

    const renderExpirationStatus = (expirationDate) => {
        if (!expirationDate) return null;
        if (isExpired(expirationDate)) {
            return <span style={{ fontSize: '10px', padding: '2px 4px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '3px', marginLeft: '4px' }}>{t('pages.stock.entry.expired', 'VENCIDO')}</span>;
        }
        if (isNearExpiry(expirationDate)) {
            return <span style={{ fontSize: '10px', padding: '2px 4px', backgroundColor: '#fff3e0', color: '#ef6c00', borderRadius: '3px', marginLeft: '4px' }}>{t('pages.stock.entry.nearExpiry', 'Próximo')}</span>;
        }
        return null;
    };

    const renderCategoryTags = (entry) => {
        const material = rawMaterials.find(r => r.id === entry.rawMaterialId);
        if (!material?.categoryIds?.length) return <span className={styles.tagSmall}>—</span>;
        const categoryNames = material.categoryIds.map(catId => categories.find(c => c.id === catId)?.name).filter(Boolean);
        if (categoryNames.length === 0) return <span className={styles.tagSmall}>—</span>;
        return (
            <>
                {categoryNames.slice(0, 2).map((name, idx) => (
                    <span key={idx} className={styles.tagSmall}>{name}</span>
                ))}
                {categoryNames.length > 2 && <span className={styles.tagSmall}>+{categoryNames.length - 2}</span>}
            </>
        );
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        setPage(1);
    };

    return (
        <div className={styles.pageContainer}>
            {/* Stock Entry Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={(e) => handleSave(entryToEdit?.id)}
                isLoading={loading}
                error={status.type === 'error' ? status.message : ''}
                success={status.type === 'success' ? status.message : ''}
                title={entryToEdit ? t('pages.stock.entry.editTitle', 'Editar Entrada de Estoque') : t('pages.stock.entry.newTitle', 'Registrar Entrada de Estoque')}
                subtitle={entryToEdit
                    ? t('pages.stock.entry.editSubtitle', 'Editando entrada de {code}', { code: entryToEdit?.rawMaterialCode || '' })
                    : t('pages.stock.entry.newSubtitle', 'Preencha os dados abaixo para registrar a entrada de materiais no estoque')}
                submitText={entryToEdit ? t('common.save', 'Salvar Alterações') : t('pages.stock.entry.register', 'Registrar Entrada')}
                maxWidth="900px"
            >
                <StockEntryModalContent
                    isEditing={!!entryToEdit}
                    rawMaterialsList={rawMaterials}
                    locationsList={locations}
                    selectedRawMaterialId={selectedRawMaterialId}
                    setSelectedRawMaterialId={setSelectedRawMaterialId}
                    selectedLocationIds={selectedLocationIds}
                    setSelectedLocationIds={setSelectedLocationIds}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    notes={notes}
                    setNotes={setNotes}
                    status={status}
                    productTypes={productTypes}
                    categories={categories}
                    filterProductTypeId={filterProductTypeId}
                    setFilterProductTypeId={setFilterProductTypeId}
                    filterCategoryId={filterCategoryId}
                    setFilterCategoryId={setFilterCategoryId}
                    locationCategoryFilter={locationCategoryFilter}
                    setLocationCategoryFilter={setLocationCategoryFilter}
                    lotNumber={lotNumber}
                    setLotNumber={setLotNumber}
                    expirationDate={expirationDate}
                    setExpirationDate={setExpirationDate}
                    manufacturerDate={manufacturerDate}
                    setManufacturerDate={setManufacturerDate}
                    loading={loading}
                    t={t}
                />
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setDeleteTarget(null);
                }}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
                title={t('pages.stock.entry.deleteTitle', 'Excluir Entrada')}
                message={t('pages.stock.entry.deleteMessage', 'Tem certeza que deseja excluir a entrada {code} ({quantity})? Esta ação é irreversível.', {
                    code: deleteTarget?.rawMaterialCode || '',
                    quantity: (deleteTarget?.quantity || 0).toFixed(3)
                })}
                confirmText={t('common.delete', 'Excluir')}
                cancelText={t('common.cancel', 'Cancelar')}
                danger
            />

            {/* Import Modal */}
            <StockImportModal
                isOpen={isImportModalOpen}
                onClose={() => {
                    setIsImportModalOpen(false);
                    setImportFile(null);
                    setImportCategory('');
                }}
                onImport={handleProcessImport}
                isImporting={isImporting}
                file={importFile}
                setFile={setImportFile}
                category={importCategory}
                setCategory={setImportCategory}
                status={status}
                setStatus={setStatus}
                t={t}
            />

            {/* Page Header */}
            <PageHeader
                title={t('pages.stock.entry.title', 'Histórico de Entradas de Estoque')}
                actionText={can.create ? t('pages.stock.entry.registerButton', 'Registrar Entrada') : undefined}
                onAction={can.create ? handleAddClick : undefined}
                disabled={loading || (!staticDataLoaded.current && rawMaterials.length === 0)}
                showAction={!!can.create}
            />

            {/* Status Toast */}
            {status.message && !isModalOpen && !isImportModalOpen && (
                <Toast
                    message={status.message}
                    type={status.type}
                    onClose={() => setStatus({ message: '', type: '' })}
                />
            )}

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <div className={styles.inputGroup}>
                    <label>{t('common.search', 'Buscar')}</label>
                    <SearchBar
                        placeholder={t('pages.stock.entry.searchPlaceholder', 'Código, nome, lote, observação...')}
                        onSearch={handleSearch}
                        disabled={loadingEntries}
                        debounceDelay={DEBOUNCE_DELAY}
                    />
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableCard}>
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{t('pages.stock.entry.tableDate', 'Data')}</th>
                                <th>{t('pages.stock.entry.tableCode', 'Código')}</th>
                                <th>{t('pages.stock.entry.tableName', 'Nome')}</th>
                                <th>{t('pages.stock.entry.tableLot', 'Lote')}</th>
                                <th>{t('pages.stock.entry.tableExpiration', 'Validade')}</th>
                                <th>{t('pages.stock.entry.tableCategories', 'Categorias')}</th>
                                <th>{t('pages.stock.entry.tableLocations', 'Localizações')}</th>
                                <th>{t('pages.stock.entry.tableQuantity', 'Qtd.')}</th>
                                <th>{t('pages.stock.entry.tableNotes', 'Observação')}</th>
                                <th>{t('pages.stock.entry.tableActions', 'Ações')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingEntries && stockEntries.length === 0 ? (
                                <TableSkeleton columns={10} rows={5} />
                            ) : (
                                currentEntries.map((entry, idx) => (
                                    <tr key={entry.id} ref={idx === currentEntries.length - 1 ? lastElementRef : null}>
                                        <td>{formatTimestamp(entry.createdAt)}</td>
                                        <td>{entry.rawMaterialCode}</td>
                                        <td>{entry.rawMaterialName}</td>
                                        <td>{entry.lotNumber || '—'}</td>
                                        <td>
                                            {entry.expirationDate ? formatDate(entry.expirationDate) : '—'}
                                            {renderExpirationStatus(entry.expirationDate)}
                                        </td>
                                        <td>{renderCategoryTags(entry)}</td>
                                        <td>
                                            {entry.isWater ? t('pages.stock.entry.water', 'Água') : (entry.locationIds?.slice(0, 2).join(', ') + (entry.locationIds?.length > 2 ? ` +${entry.locationIds.length - 2}` : ''))}
                                        </td>
                                        <td>{(entry.quantity || 0).toFixed(3)}</td>
                                        <td>{entry.notes?.substring(0, 50) || '—'}</td>
                                        <td className={styles.actionCell}>
                                            {can.edit && (
                                                <button
                                                    onClick={() => handleEditClick(entry)}
                                                    className={`${styles.actionButton} ${styles.editButton}`}
                                                    title={t('common.edit', 'Editar')}
                                                >
                                                    <FiEdit size={18} />
                                                </button>
                                            )}
                                            {can.delete && (
                                                <button
                                                    onClick={() => handleDeleteClick(entry)}
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    title={t('common.delete', 'Excluir')}
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                            {!loadingEntries && currentEntries.length === 0 && (
                                <tr>
                                    <td colSpan="10">
                                        <EmptyState
                                            title={t('pages.stock.entry.noEntries', 'Nenhuma entrada encontrada')}
                                            message={debouncedSearchTerm ? t('pages.stock.entry.tryDifferentSearch', 'Tente ajustar os filtros de busca') : t('pages.stock.entry.startAdding', 'Comece registrando uma nova entrada')}
                                            actionText={can.create ? t('pages.stock.entry.registerButton', 'Registrar Entrada') : undefined}
                                            onAction={can.create ? handleAddClick : undefined}
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalFilteredItems}
                    onPageChange={handlePageChange}
                    disabled={loadingEntries}
                    itemsLabel={t('pages.stock.entry.entries', 'entradas')}
                />
            </div>
        </div>
    );
};

export default StockEntryPage;